
import { Cloud, Zap, Shield, Bell, Clock, Target } from "lucide-react";
import { LandingPageTemplate } from "@/components/layout/LandingPageTemplate";
import { HeroSection } from "@/components/landing/HeroSection";
import { FeatureSection } from "@/components/landing/FeatureSection";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { BenefitSection } from "@/components/landing/BenefitSection";
import { CtaSection } from "@/components/landing/CtaSection";

const CloudSoar = () => {
  return (
    <LandingPageTemplate
      title="Cloud SOAR Platform"
      description="Advanced Security Orchestration, Automation and Response (SOAR) platform for cloud environments."
      keywords={[
        "cloud SOAR",
        "security automation",
        "incident response",
        "security orchestration",
        "threat response"
      ]}
      canonicalUrl="/cybersecurity/cloud-soar/"
      schemaType="service"
      schemaData={{
        name: "Cloud SOAR Platform",
        description: "Advanced cloud security orchestration and automation",
        provider: {
          name: "edgefi",
          sameAs: "https://edgefi.com"
        }
      }}
    >
      <HeroSection
        title="Cloud Security"
        highlighted="Automation"
        description="Streamline your security operations with our advanced Cloud SOAR platform for automated incident response and threat management."
        ctaText="Get Started"
        heroPattern="dots"
      />

      <FeatureSection 
        title="Cloud SOAR Features"
        subtitle="KEY CAPABILITIES"
        description="Comprehensive security orchestration and automation for cloud environments."
        features={[
          {
            title: "Cloud Integration",
            description: "Seamless integration with cloud platforms.",
            icon: <Cloud className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Automated Response",
            description: "Rapid automated incident handling.",
            icon: <Zap className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Security Orchestration",
            description: "Coordinated security tool management.",
            icon: <Shield className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Alert Management",
            description: "Centralized alert handling and triage.",
            icon: <Bell className="h-6 w-6 text-purple-600" />
          },
          {
            title: "24/7 Monitoring",
            description: "Continuous security monitoring.",
            icon: <Clock className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Threat Hunting",
            description: "Proactive threat detection.",
            icon: <Target className="h-6 w-6 text-purple-600" />
          }
        ]}
      />

      <ProcessSection
        title="Implementation Process"
        subtitle="METHODOLOGY"
        description="Structured approach to implementing Cloud SOAR"
        steps={[
          {
            title: "Assessment",
            description: "Evaluate current security operations"
          },
          {
            title: "Integration",
            description: "Connect cloud platforms and tools"
          },
          {
            title: "Automation",
            description: "Configure automated workflows"
          },
          {
            title: "Testing",
            description: "Validate response procedures"
          },
          {
            title: "Optimization",
            description: "Continuous improvement"
          }
        ]}
      />

      <BenefitSection
        title="Why Choose Our Cloud SOAR"
        benefits={[
          {
            title: "Rapid Response",
            description: "Automated incident handling"
          },
          {
            title: "Efficiency",
            description: "Streamlined operations"
          },
          {
            title: "Integration",
            description: "Multi-cloud support"
          },
          {
            title: "Scalability",
            description: "Grows with your needs"
          },
          {
            title: "Visibility",
            description: "Comprehensive reporting"
          },
          {
            title: "Cost Effective",
            description: "Reduced manual effort"
          }
        ]}
      />

      <CtaSection 
        title="Ready to Automate Your Security?"
        description="Transform your security operations with Cloud SOAR."
        ctaText="Schedule Demo"
        background="purple"
        modalTitle="Request Cloud SOAR Demo"
        modalDescription="Tell us about your security automation needs."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "Cloud SOAR",
          service_interest: "Security Automation"
        }}
      />
    </LandingPageTemplate>
  );
};

export default CloudSoar;
